import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { crmApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  TrophyIcon,
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon,
} from 'components/icons';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { SkeletonCards, SkeletonRows } from '../../components/ui/Skeleton';
import PageHeader from '../../components/ui/PageHeader';

interface Contact {
  id: string;
  name: string;
  title?: string;
  agency?: string;
  email?: string;
  phone?: string;
  type: string;
  notes?: string;
  lastContactAt?: string;
  createdAt: string;
}

interface Metrics {
  pipeline: {
    totalOpportunities: number;
    activeOpportunities: number;
    totalValue: number;
    byStage: Record<string, { count: number; value: number }>;
  };
  winRate: {
    allTime: number;
    ytd: number;
    awarded: number;
    lost: number;
  };
  upcoming: {
    dueThisWeek: number;
    dueThisMonth: number;
  };
  topAgencies: { agency: string; count: number }[];
  recentActivity: { type: string; description: string; date: string }[];
}

const CONTACT_TYPES = [
  { value: 'government', label: 'Government' },
  { value: 'teaming_partner', label: 'Teaming Partner' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'other', label: 'Other' },
];

const CRM: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [metricsError, setMetricsError] = useState(false);
  const [contactsError, setContactsError] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'contacts'>('overview');
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics();
    fetchContacts();
  }, []);

  const fetchMetrics = async () => {
    setLoadingMetrics(true);
    setMetricsError(false);
    try {
      const response = await crmApi.getDashboard();
      setMetrics(response.data.metrics);
    } catch (error) {
      // Was "silently fail", which rendered a metrics row of zeroes that read
      // as real data.
      console.error('CRM metrics failed:', error);
      setMetricsError(true);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    setContactsError(false);
    try {
      const response = await crmApi.getContacts();
      setContacts(response.data.contacts || []);
    } catch (error) {
      // Was "silently fail", indistinguishable from having no contacts.
      console.error('CRM contacts failed:', error);
      setContactsError(true);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleAddContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSavingContact(true);
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await crmApi.addContact(data);
      setContacts(prev => [response.data.contact, ...prev]);
      setShowAddContact(false);
      toast.success('Contact added');
      (e.target as HTMLFormElement).reset();
    } catch {
      toast.error('Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (id: string) => {
    setDeletingId(id);
    try {
      await crmApi.deleteContact(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact deleted');
    } catch {
      toast.error('Failed to delete contact');
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredContacts = contactSearch.trim()
    ? contacts.filter(c =>
        c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.agency?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.title?.toLowerCase().includes(contactSearch.toLowerCase())
      )
    : contacts;

  const stageOrder = ['new', 'analyzing', 'qualified', 'proposal', 'submitted', 'awarded', 'lost', 'archived'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
        title="CRM"
        subtitle="Pipeline overview, metrics, and contacts"
      />
        <div className="flex items-center gap-2">
          <Link to="/pipeline?view=board" className="btn-secondary text-sm">View Pursuit Board</Link>
          <Link to="/opportunities" className="btn-primary text-sm flex items-center gap-1">
            <PlusIcon className="h-4 w-4" /> Find Opportunities
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 rounded-xl bg-surface-sunk p-1 max-w-xs">
        <button
          onClick={() => setActiveTab('overview')}
          className={`w-full rounded-lg py-2 text-sm font-medium ${
            activeTab === 'overview' ? 'bg-white text-brand-700 shadow' : 'text-content-muted hover:text-content'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`w-full rounded-lg py-2 text-sm font-medium ${
            activeTab === 'contacts' ? 'bg-white text-brand-700 shadow' : 'text-content-muted hover:text-content'
          }`}
        >
          Contacts ({contacts.length})
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Stats Grid */}
          {loadingMetrics ? (
            <SkeletonCards count={4} />
          ) : metricsError ? (
            <div className="card">
              <ErrorState
                title="Couldn't load CRM metrics"
                onRetry={fetchMetrics}
                retrying={loadingMetrics}
                compact
              />
            </div>
          ) : metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface p-5 rounded-lg shadow">
                <div className="flex items-center gap-2 mb-1">
                  <ChartBarIcon className="h-4 w-4 text-brand-500" />
                  <p className="text-sm text-content-muted">Active Pipeline</p>
                </div>
                <p className="stat-value text-content">{metrics.pipeline.activeOpportunities}</p>
                <p className="text-xs text-content-subtle mt-1">{metrics.pipeline.totalOpportunities} total</p>
              </div>
              <div className="bg-surface p-5 rounded-lg shadow">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyDollarIcon className="h-4 w-4 text-green-500" />
                  <p className="text-sm text-content-muted">Pipeline Value</p>
                </div>
                <p className="stat-value text-content">{formatCurrency(metrics.pipeline.totalValue)}</p>
              </div>
              <div className="bg-surface p-5 rounded-lg shadow">
                <div className="flex items-center gap-2 mb-1">
                  <TrophyIcon className="h-4 w-4 text-amber-500" />
                  <p className="text-sm text-content-muted">Win Rate (YTD)</p>
                </div>
                <p className="stat-value text-content">{metrics.winRate.ytd}%</p>
                <p className="text-xs text-content-subtle mt-1">{metrics.winRate.awarded}W / {metrics.winRate.lost}L</p>
              </div>
              <div className="bg-surface p-5 rounded-lg shadow">
                <div className="flex items-center gap-2 mb-1">
                  <ClockIcon className="h-4 w-4 text-red-500" />
                  <p className="text-sm text-content-muted">Due This Week</p>
                </div>
                <p className="stat-value text-content">{metrics.upcoming.dueThisWeek}</p>
                <p className="text-xs text-content-subtle mt-1">{metrics.upcoming.dueThisMonth} this month</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stage Breakdown */}
            <div className="lg:col-span-2 bg-surface shadow rounded-lg p-6">
              <h3 className="font-medium text-content mb-4">Pipeline by Stage</h3>
              {metrics?.pipeline.byStage && Object.keys(metrics.pipeline.byStage).length > 0 ? (
                <div className="space-y-3">
                  {stageOrder
                    .filter(stage => metrics.pipeline.byStage[stage])
                    .map(stage => {
                      const data = metrics.pipeline.byStage[stage];
                      const pct = metrics.pipeline.totalOpportunities > 0
                        ? Math.round((data.count / metrics.pipeline.totalOpportunities) * 100)
                        : 0;
                      return (
                        <div key={stage} className="flex items-center gap-3">
                          <span className="text-sm text-content-muted w-24 capitalize">{stage}</span>
                          <div className="flex-1 bg-surface-sunk rounded-full h-4 overflow-hidden">
                            <div
                              className="h-full bg-brand-500 rounded-full transition-all"
                              style={{ width: `${Math.max(pct, 2)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-content w-8 text-right">{data.count}</span>
                          <span className="text-xs text-content-subtle w-16 text-right">{formatCurrency(data.value)}</span>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-content-subtle text-sm">No pipeline data yet. <Link to="/opportunities" className="text-brand-600 hover:underline">Add opportunities</Link> to see stats.</p>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Top Agencies */}
              {metrics?.topAgencies && metrics.topAgencies.length > 0 && (
                <div className="bg-surface shadow rounded-lg p-6">
                  <h3 className="font-medium text-content mb-3">Top Agencies</h3>
                  <div className="space-y-2">
                    {metrics.topAgencies.map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-content truncate mr-2">{a.agency}</span>
                        <span className="text-content-subtle flex-shrink-0">{a.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="bg-surface shadow rounded-lg p-6">
                <h3 className="font-medium text-content mb-3">Recent Activity</h3>
                {metrics?.recentActivity && metrics.recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {metrics.recentActivity.slice(0, 5).map((activity, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-1.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-content">{activity.description}</p>
                          <p className="text-xs text-content-subtle">{formatDate(activity.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-content-subtle text-sm">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'contacts' && (
        <div className="space-y-4">
          {/* Contacts Header */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Search contacts..."
                className="input w-full max-w-md"
              />
            </div>
            <button
              onClick={() => setShowAddContact(!showAddContact)}
              className="btn-primary flex items-center gap-1 text-sm"
            >
              {showAddContact ? <XMarkIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
              {showAddContact ? 'Cancel' : 'Add Contact'}
            </button>
          </div>

          {/* Add Contact Form */}
          {showAddContact && (
            <div className="bg-surface shadow rounded-lg p-6">
              <h3 className="font-medium text-content mb-4">New Contact</h3>
              <form onSubmit={handleAddContact} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Name *</label>
                  <input name="name" required className="input" placeholder="Full name" />
                </div>
                <div>
                  <label className="label">Title</label>
                  <input name="title" className="input" placeholder="e.g. Contracting Officer" />
                </div>
                <div>
                  <label className="label">Agency / Company</label>
                  <input name="agency" className="input" placeholder="e.g. Department of Defense" />
                </div>
                <div>
                  <label className="label">Type</label>
                  <select name="type" className="input" defaultValue="government">
                    {CONTACT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Email</label>
                  <input name="email" type="email" className="input" placeholder="email@agency.gov" />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input name="phone" className="input" placeholder="(555) 123-4567" />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Notes</label>
                  <textarea name="notes" className="input" rows={2} placeholder="Any notes about this contact..." />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={savingContact} className="btn-primary disabled:opacity-50">
                    {savingContact ? 'Saving...' : 'Save Contact'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Contacts List */}
          {loadingContacts ? (
            <div className="card">
              <SkeletonRows rows={5} cols={3} />
            </div>
          ) : contactsError ? (
            <div className="card">
              <ErrorState
                title="Couldn't load contacts"
                onRetry={fetchContacts}
                retrying={loadingContacts}
              />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="card">
              <EmptyState
                icon={UserGroupIcon}
                title={contactSearch.trim() ? 'No matching contacts' : 'No contacts yet'}
                description={
                  contactSearch.trim()
                    ? 'Try a different name or company.'
                    : 'Add contracting officers, teaming partners, and other key contacts.'
                }
              />
            </div>
          ) : (
            <div className="bg-surface shadow rounded-lg divide-y divide-border">
              {filteredContacts.map((contact) => (
                <div key={contact.id} className="px-6 py-4 flex items-center justify-between hover:bg-surface-sunk">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-brand-700 font-medium text-sm">
                        {contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-content">{contact.name}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-surface-sunk text-content-muted capitalize">
                          {contact.type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-content-muted mt-0.5">
                        {contact.title && <span>{contact.title}</span>}
                        {contact.agency && (
                          <span className="flex items-center gap-1">
                            <BuildingOfficeIcon className="h-3 w-3" />
                            {contact.agency}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-content-subtle mt-0.5">
                        {contact.email && (
                          <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-brand-600">
                            <EnvelopeIcon className="h-3 w-3" />
                            {contact.email}
                          </a>
                        )}
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <PhoneIcon className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteContact(contact.id)}
                    disabled={deletingId === contact.id}
                    className="p-2 text-content-subtle hover:text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                    title="Delete contact"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CRM;
